<template>

  <card-fieldset :style="'margin-left: '+level+'px; min-width: 100%;'" class="card-fieldset" v-if="checklist" :key="checklist.id" :collapsed="getCollapsed(checklist.id)" :reference-id="checklist.id">
    <template #legendSlot>
      <div>
        <span class="p-fieldset-legend-text" :title="$t(checklist.category === 'IMMIGRANT' ? 'protocol.tab-checklist.immigrant' : 'protocol.tab-checklist.ascendant')">
            <i class="fa-solid fa-plane-departure p-mr-1" v-if="checklist.category === 'IMMIGRANT'"/>
            <i class="fa-solid fa-user" v-else />
            {{checklist.person.name}} <i v-if="getClientContainsError(checklist.person.id)" class="fa-solid fa-triangle-exclamation client-contain-errors"></i>
        </span>
        <ancestryline-progress-bar :value="calculateProgressCertificates(checklist.certificates)" />
      </div>
    </template>
    <h2>{{ getCategoryCertificateName(checklist.category) }}</h2>
    <div class="actions" v-if="!sent">
      <Button icon="pi pi-user-edit small" class="p-button-label p-mr-2 small" :label="$t('protocol.tab-checklist.edit-personal-data')" @click.prevent="editPersonalData(checklist)"/>
      <Button icon="fa-solid fa-check small" class="p-button-label p-mr-2 small" :label="$t('protocol.tab-checklist.edit-checklist')" @click.prevent="openModalAddChecklistProtocolPerson(checklist)"/>
      <Button icon="pi pi-trash small" class="p-button-danger p-mr-2 small" :label="$t('protocol.tab-checklist.delete-checklist-person')" @click.prevent="deleteChecklistProtocolPerson(checklist)"/>
    </div>
    <Message class="p-col-12" v-for="msg of getClientErrors(checklist.person.id)" :severity="msg.type" :key="checklist.person.id + msg.message">{{msg.message}}</Message>
    <template v-if="checklist.certificates.length > 0">
        <certificate v-for="(certificate, index) in orderCertificates(checklist.certificates)" :certificate="certificate" :person="checklist.person" :errors="getCertificateClientErrors(checklist.person.id, certificate.id)" :certificate-order="index % 2" :key="`${checklist.id}-certificate-${index}`" :canDelete="!sent"/>
    </template>
    <template v-else>
        <Message class="p-col-12" key="certificate">{{$t('protocol.tab-checklist.no-certificates')}}</Message>
    </template>
    <div class="actions-add-certificate" v-if="!sent">
        <Button icon="pi pi-plus small" :label="$t('protocol.tab-checklist.add-certificate')" @click.prevent="newCertificate(checklist)" />
    </div>
    <template v-for="(checklistChildren, index) in checklist.children">
      <checklist-protocol-person
      :key="index"
      :protocolId="protocolId" 
      :sent="sent"
      :checklist="checklistChildren" 
      :clientsErrors="clientsErrors"
      :clientsProtocol="clientsProtocol"
      :checklistProtocolPerson="checklistProtocolPerson"
      :level="level+10" />
      </template>
  </card-fieldset>

</template>

<script>

import CertificateProtocolBusiness from "@/business/CertificateProtocolBusiness";
import Certificate from "@/viewer/protocol/tabs/checklist/Certificate";
import CardFieldset from "@/components/card/fieldset/CardFieldset";
import {BIRTH, DEATH, IDENTIFICATION, WEDDING, ADDRESS_PROOF, REFERENCE, CNN} from "@/constants/TypeOfCertificates";
import ToastMessageBusiness from "@/business/ToastMessageBusiness";
import EventBus from "@/commons/EventBus";
import DialogService from "@/business/ModalBusiness";
import {APPLICANT} from "@/constants/CategoryOfCertificates";
import CategoryOfCertificates from "@/constants/CategoryOfCertificates";
import store from "@/store";
import AncestrylineConfirmation from "@/components/dialog/AncestrylineConfirmation";
import ChecklistProtocolPersonBusiness from "@/business/ChecklistProtocolPersonBusiness";
import ModalClientForm from "@/viewer/client/ModalClientForm";

export default {

  name: "ChecklistProtocolPerson",
  components: {CardFieldset, Certificate},
  props: ["protocolId", "sent", "checklist", "clientsErrors", "clientsProtocol", "checklistProtocolPerson", "level"],

  mounted() {
    EventBus.$on('update:collapsed', this.listenCollapsed);
  },

  data() {
    return {
      collapsedItens: store.getChecklistCollapsed(this.$route.params.id) || {},
      getCategoryCertificateName: CategoryOfCertificates.getCategoryCertificateName,
    }
  },

  methods: {

    calculateProgressCertificates: CertificateProtocolBusiness.calculateProgressCertificates,

    orderCertificates(certificates){
      const reference = certificates.filter(certificate => certificate.typeOfCertificate === REFERENCE);
      const births = certificates.filter(certificate => certificate.typeOfCertificate === BIRTH);
      const weddings = certificates.filter(certificate => certificate.typeOfCertificate === WEDDING);
      const identification = certificates.filter(certificate => certificate.typeOfCertificate === IDENTIFICATION);
      const deaths = certificates.filter(certificate => certificate.typeOfCertificate === DEATH);
      const addressProof = certificates.filter(certificate => certificate.typeOfCertificate === ADDRESS_PROOF);
      const cnn = certificates.filter(certificate => certificate.typeOfCertificate === CNN);
      const others = certificates.filter(certificate => !certificate.typeOfCertificate);
      return [...reference, ...births, ...weddings, ...identification, ...deaths, ...addressProof, ...cnn, ...others];
    },

    async editPersonalData(checklistProtocolPerson){
        const response = await DialogService.openModal(ModalClientForm, {clientId: checklistProtocolPerson.person.id}, 1000);
        if (response) {
          EventBus.$emit('loadChecklistProtocolPeople');
        }
    },

    getClientContainsError(clientID){
        const errorClientFounded = this.clientsErrors.filter(clientError => clientError.clientId === clientID);
        if(errorClientFounded[0]) {
            const hasErrors = errorClientFounded[0].errors.length > 0;
            const hasCertificateErrors = errorClientFounded[0].certificateErrors && Object.keys(errorClientFounded[0].certificateErrors).length > 0;
            return hasErrors || hasCertificateErrors;
        }
        return false;
    },

    async openModalAddChecklistProtocolPerson(checklistPersonToEdit){
      EventBus.$emit('openModalAddChecklistProtocolPerson', checklistPersonToEdit);
    },

    createEmptyChecklistProtocolPerson(checklist){
      return {
        person: checklist.person,
        parent: checklist.parent,
        protocol: checklist.protocol,
        category: checklist.category || APPLICANT,
        certificates: checklist.certificates || [],
      }
    },

    async deleteChecklistProtocolPerson(checklistProtocolPerson){
        if(await DialogService.openConfirmationModal(AncestrylineConfirmation, {message: "protocol.tab-checklist.confirm-delete-checklist-person"})){
            const response = await ChecklistProtocolPersonBusiness.delete(checklistProtocolPerson.id).catch((e) => {
                if(e.checklist_protocol_person_constraint){
                    ToastMessageBusiness.showError(e.checklist_protocol_person_constraint)
                } else {
                    ToastMessageBusiness.showError(e.message)
                }
            });
            if(response){
              EventBus.$emit('loadChecklistProtocolPeople');
            }
        }
    },

    getClientErrors(clientID) {
      const errorClientFounded = this.clientsErrors.filter(clientError => clientError.clientId === clientID);
      if (errorClientFounded[0]) {
          return errorClientFounded[0].errors;
      } else {
          return [];
      }
    },

    getCertificateClientErrors(clientID, certificateId) {
        const errorClientFounded = this.clientsErrors.filter(clientError => clientError.clientId === clientID);
        if(errorClientFounded[0]){
            return errorClientFounded[0].certificateErrors[certificateId] || [];
        }
        return [];
    },

    newCertificate(checklist) {
      checklist.certificates.push({checklistProtocolPerson: {id: checklist.id}});
    },

    listenCollapsed(item){
      this.collapsedItens[item.referenceId] = item.collapsed;
      store.setChecklistCollapsed(this.protocolId, this.collapsedItens);
    },

    getCollapsed(clientId){
      return this.collapsedItens[clientId] || false;
    },
    
  },

  beforeDestroy() {
    EventBus.$off('update:collapsed');
  }
  
}
</script>

<style scoped>

  @keyframes pulse {
    0% {
        transform: scale(1);
    }
    50% {
        transform: scale(1.2);
    }
    100% {
        transform: scale(1);
    }
  }

  .actions-add-certificate {
      text-align: right;
      margin-bottom: 10px;
  }

  .actions {
    text-align: right;
    margin-top: -35px;
    margin-bottom: 10px;
  }

  .client-contain-errors {
      color: #e40021;
      animation: pulse 1s infinite;
  }

</style>